'use client';

import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Video, Pause, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api/client';
import { MediaImage } from '@/components/ui/media-image';
import type { SharedStream } from '@/lib/shared-types';

interface RelatedStreamsCarouselProps {
  currentStreamId: number;
  tags?: string[];
  className?: string;
}

const CACHE_KEY_PREFIX = 'related_streams_cache:';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CachedData {
  streams: SharedStream[];
  timestamp: number;
}

function getCachedStreams(streamId: number): SharedStream[] | null {
  try {
    const cached = localStorage.getItem(`${CACHE_KEY_PREFIX}${streamId}`);
    if (!cached) return null;

    const data: CachedData = JSON.parse(cached);
    const isExpired = Date.now() - data.timestamp > CACHE_TTL_MS;

    if (isExpired) {
      localStorage.removeItem(`${CACHE_KEY_PREFIX}${streamId}`);
      return null;
    }

    return data.streams;
  } catch {
    return null;
  }
}

function setCachedStreams(streamId: number, streams: SharedStream[]): void {
  try {
    const data: CachedData = { streams, timestamp: Date.now() };
    localStorage.setItem(`${CACHE_KEY_PREFIX}${streamId}`, JSON.stringify(data));
  } catch {
    // Ignore localStorage errors (quota exceeded, etc.)
  }
}

export function RelatedStreamsCarousel({
  currentStreamId,
  tags = [],
  className = ''
}: RelatedStreamsCarouselProps) {
  const navigate = useNavigate();
  const [relatedStreams, setRelatedStreams] = useState<SharedStream[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const autoplayTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const fetchRelatedStreams = async () => {
      // Check localStorage cache first for instant display
      const cached = getCachedStreams(currentStreamId);
      if (cached) {
        setRelatedStreams(cached);
        setIsInitialLoad(false);
      }

      try {
        const response = await api.get<{ relatedStreams: SharedStream[] }>(
          `/api/streams/${currentStreamId}/related`
        );

        if (response.success && response.data) {
          const streams = response.data.relatedStreams.slice(0, 10);
          setRelatedStreams(streams);
          setCachedStreams(currentStreamId, streams);
        }
      } catch (error) {
        console.error('Failed to fetch related streams:', error);
      } finally {
        setIsInitialLoad(false);
      }
    };

    if (currentStreamId) {
      fetchRelatedStreams();
    }

    // Cleanup autoplay on unmount
    return () => {
      if (autoplayTimerRef.current) {
        clearInterval(autoplayTimerRef.current);
        autoplayTimerRef.current = null;
      }
    };
  }, [currentStreamId]);

  const loading = isInitialLoad && relatedStreams.length === 0;

  // Setup autoplay timer
  useEffect(() => {
    if (relatedStreams.length <= 1 || isPaused) {
      if (autoplayTimerRef.current) {
        clearInterval(autoplayTimerRef.current);
        autoplayTimerRef.current = null;
      }
      return;
    }

    if (!autoplayTimerRef.current) {
      autoplayTimerRef.current = setInterval(() => {
        const container = document.getElementById(`related-streams-${currentStreamId}`);
        if (!container) return;

        const containerWidth = container.clientWidth;
        const scrollAmount = containerWidth * 0.8;
        const maxScroll = container.scrollWidth - containerWidth;
        let newPosition = scrollPosition + scrollAmount;

        // If we're at the end, loop back to start
        if (newPosition >= maxScroll - 10) {
          newPosition = 0;
          container.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          container.scrollTo({ left: newPosition, behavior: 'smooth' });
        }

        setScrollPosition(newPosition);
      }, 3000); // Auto-scroll every 3 seconds
    }

    return () => {
      if (autoplayTimerRef.current) {
        clearInterval(autoplayTimerRef.current);
        autoplayTimerRef.current = null;
      }
    };
  }, [relatedStreams.length, currentStreamId, scrollPosition, isPaused]);

  const handleScroll = (direction: 'left' | 'right') => {
    const container = document.getElementById(`related-streams-${currentStreamId}`);
    if (!container) return;

    const scrollAmount = container.clientWidth * 0.8;
    const newPosition = direction === 'left'
      ? Math.max(0, scrollPosition - scrollAmount)
      : scrollPosition + scrollAmount;

    container.scrollTo({ left: newPosition, behavior: 'smooth' });
    setScrollPosition(newPosition);

    // Pause autoplay when user manually scrolls
    if (!isPaused) {
      setIsPaused(true);
    }
  };

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  const handleCarouselClick = () => {
    if (!isPaused) {
      setIsPaused(true);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'live':
        return <Badge variant="destructive" className="animate-pulse text-xs">LIVE</Badge>;
      case 'scheduled':
        return <Badge variant="secondary" className="text-xs">Scheduled</Badge>;
      case 'ended':
        return <Badge variant="outline" className="text-xs">Ended</Badge>;
      default:
        return null;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Up Next</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="flex gap-3 px-10 pb-3 overflow-hidden">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex-shrink-0 w-64 animate-pulse">
                <div className="aspect-video rounded-lg bg-muted mb-2" />
                <div className="space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (relatedStreams.length === 0) {
    return null; // Don't show carousel if no related streams
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Up Next</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="relative group">
          {/* Left scroll button */}
          <Button
            variant="secondary"
            size="icon"
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
            onClick={() => handleScroll('left')}
            disabled={scrollPosition <= 0}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Pause/Play button at bottom right */}
          {relatedStreams.length > 1 && (
            <Button
              variant="secondary"
              size="icon"
              className="absolute bottom-3 right-3 z-10 bg-black/70 hover:bg-black/90 text-white h-6 w-6 rounded-full"
              onClick={togglePause}
              title={isPaused ? "Resume autoplay" : "Pause autoplay"}
            >
              {isPaused ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
            </Button>
          )}

          {/* Scrollable container */}
          <div
            id={`related-streams-${currentStreamId}`}
            className="flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth px-10 pb-3 cursor-pointer"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onClick={handleCarouselClick}
          >
            {relatedStreams.map((stream) => (
              <div
                key={stream.id}
                onClick={() => navigate(`/streams/${stream.id}`)}
                className="flex-shrink-0 w-64 cursor-pointer group/item"
              >
                <div className="relative aspect-video rounded-lg overflow-hidden bg-muted mb-2">
                  {stream.thumbnailUrl ? (
                    <MediaImage
                      src={stream.thumbnailUrl}
                      alt={stream.title}
                      className="w-full h-full object-cover group-hover/item:scale-105 transition-transform duration-200"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-muted">
                      <Video className="h-8 w-8 text-muted-foreground" />
                    </div>
                  )}

                  {/* Status badge overlay */}
                  <div className="absolute top-2 left-2">
                    {getStatusBadge(stream.status)}
                  </div>

                  {/* Duration badge */}
                  {stream.duration && stream.status === 'ended' && (
                    <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-0.5 rounded">
                      {Math.floor(stream.duration / 60)}:{(stream.duration % 60).toString().padStart(2, '0')}
                    </div>
                  )}
                </div>

                <div className="space-y-1">
                  <h4 className="font-medium text-sm line-clamp-2 leading-tight">
                    {stream.title}
                  </h4>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{stream.creator?.name}</span>
                    <span>•</span>
                    <span>{formatDate(stream.scheduledStart)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{stream.viewerCount || 0} views</span>
                    {stream.likes !== undefined && (
                      <>
                        <span>•</span>
                        <span>{stream.likes} likes</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right scroll button */}
          <Button
            variant="secondary"
            size="icon"
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
            onClick={() => handleScroll('right')}
            disabled={scrollPosition >= relatedStreams.length * 300 - 300}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
